import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatApiError } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { UserCircleGear, Plus, Trash, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api`, withCredentials: true });

/**
 * Karte in Einstellungen: Buchhalter/Steuerberater-Zugänge verwalten.
 * Sichtbar nur für Admin.
 */
export default function AccountantAccessCard({ currentUserRole }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/users');
      setUsers((r.data || []).filter(u => u.role === 'accountant'));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (currentUserRole === 'admin') load(); /* eslint-disable-next-line */ }, [currentUserRole]);

  const create = async () => {
    if (!form.email || !form.password) { toast.error('E-Mail und Passwort erforderlich'); return; }
    if (form.password.length < 8) { toast.error('Passwort mind. 8 Zeichen'); return; }
    setSaving(true);
    try {
      await api.post('/users/accountant', { ...form, role: 'accountant' });
      toast.success('Buchhalter-Zugang angelegt', { description: `${form.email} kann sich jetzt anmelden.` });
      setForm({ email: '', password: '', name: '' });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Buchhalter-Zugang für ${email} wirklich löschen?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('Zugang entfernt');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (currentUserRole !== 'admin') return null;

  return (
    <div className="card-swiss p-6" data-testid="accountant-access-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
          <UserCircleGear weight="fill" size={20} className="text-emerald-700"/>
        </div>
        <div>
          <h2 className="text-lg font-bold">Buchhalter / Steuerberater-Zugänge</h2>
          <p className="text-sm text-[#525866]">Read/Write nur auf Finanz-Module (Rechnungen, Kassenbuch, Mahnwesen, DATEV, Lieferanten). Kein Zugriff auf Fahrzeug- oder Buchungsverwaltung.</p>
        </div>
      </div>

      {/* Bestehende Zugänge */}
      {loading ? <div className="h-16 skeleton"/> : users.length > 0 ? (
        <ul className="mb-4 space-y-1">
          {users.map(u => (
            <li key={u.id} className="flex items-center justify-between bg-[#F7F8FA] rounded px-3 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck size={16} className="text-emerald-600 flex-shrink-0" weight="bold"/>
                <div className="min-w-0">
                  <p className="font-semibold text-[#111418] truncate">{u.name}</p>
                  <p className="text-xs text-[#525866] truncate">{u.email}</p>
                </div>
              </div>
              <button onClick={() => remove(u.id, u.email)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`accountant-remove-${u.id}`}>
                <Trash size={14} weight="bold"/>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-[#8A94A6] mb-4">Noch kein Buchhalter-Zugang angelegt.</p>
      )}

      {/* Neuer Account */}
      <div className="border-t border-[#E2E4E9] pt-4">
        <p className="text-sm font-semibold mb-2">Neuen Zugang anlegen</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <Label className="overline">Name</Label>
            <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="z.B. Max Steuerberater" className="input-swiss" data-testid="accountant-form-name"/>
          </div>
          <div>
            <Label className="overline">E-Mail *</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="buchhalter@stb.de" className="input-swiss" data-testid="accountant-form-email"/>
          </div>
          <div>
            <Label className="overline">Passwort * (min 8)</Label>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="••••••••" className="input-swiss" data-testid="accountant-form-password"/>
          </div>
        </div>
        <div className="mt-3">
          <Button className="btn-primary" onClick={create} disabled={saving} data-testid="accountant-create-btn">
            <Plus weight="bold" size={14} className="mr-1"/> {saving ? 'Lege an…' : 'Zugang anlegen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
